/**
 * PreprocessSection 預處理區段元件
 *
 * 提供平面圖上傳和預處理功能：
 * - 圖片上傳
 * - 預處理選項
 * - 參考線繪製
 * - 橡皮擦和塗黑線條
 */

import { Section } from '@/components/layout/Section';
import { UploadDropzone } from '@/components/shared/UploadDropzone';
import { Checkbox } from '@/components/shared/FormInput';
import { Button } from '@/components/shared/Button';
import { PreprocessCanvas } from '@/canvas/preprocess-canvas';
import {
  preprocessStore,
  setDrawingMode,
  setProcessedImage,
  setDetectedDimensions,
  setOriginalImage,
  useSelectedDimensionAsReference,
  selectDimension,
  type DrawingMode,
} from '@/store/preprocess-store';
import {
  preprocessImage,
  detectDimensionsOnly,
  type PreprocessParams,
  type FrontendDetectedDimension,
} from '@/api/preprocess-api';
import { prompt } from '@/components/layout/Modal';
import { distance } from '@/utils/geometry';

// ========================================================================
// 型別定義
// ========================================================================

export interface PreprocessSectionOptions {
  /** 預處理完成回呼 */
  onComplete?: (processedImage: string, pixelToMeter: number) => void;
}

// ========================================================================
// PreprocessSection 類別
// ========================================================================

export class PreprocessSection {
  private section: Section;
  private uploadZone: UploadDropzone;
  private canvas: PreprocessCanvas | null = null;
  private options: PreprocessSectionOptions;
  private unsubscribe: (() => void) | null = null;

  // 狀態
  private originalFile: File | null = null;
  private originalImageDataUrl: string | null = null;

  // UI 元素
  private uploadContainer: HTMLDivElement;
  private optionsContainer: HTMLDivElement;
  private processingContainer: HTMLDivElement;
  private resultContainer: HTMLDivElement;
  private errorContainer: HTMLDivElement;
  private startButton: Button | null = null;
  private modeButtons: Map<DrawingMode, HTMLButtonElement> = new Map();

  // 選項
  private removeTextCheckbox: Checkbox;
  private hasDimensionsCheckbox: Checkbox;

  constructor(options: PreprocessSectionOptions = {}) {
    this.options = options;

    // 建立區段
    this.section = new Section({
      title: '步驟 1：上傳平面圖',
      description: '上傳您的平面圖，系統將自動處理並提取可用空間',
      stepNumber: 1,
    });

    // 建立容器
    this.uploadContainer = this.createUploadContainer();
    this.optionsContainer = this.createOptionsContainer();
    this.processingContainer = this.createProcessingContainer();
    this.resultContainer = this.createResultContainer();
    this.errorContainer = this.createErrorContainer();

    // 建立上傳區域
    this.uploadZone = new UploadDropzone({
      accept: 'image/*',
      onFileSelect: this.handleFileSelect.bind(this),
      onError: (error) => console.error(error),
    });

    // 建立選項（預設不勾選）
    this.removeTextCheckbox = new Checkbox('移除文字和標線', { checked: false });
    this.hasDimensionsCheckbox = new Checkbox('圖片有長度標示', { checked: false });

    // 組裝 UI
    this.assembleUI();
    this.subscribeToStore();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLElement {
    return this.section.getElement();
  }

  mount(container: HTMLElement): void {
    this.section.mount(container);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.canvas?.destroy();
    this.section.destroy();
  }

  /**
   * 從儲存的規劃載入圖片
   */
  loadFromSavedPlan(originalDataUrl: string, processedDataUrl: string): void {
    // 隱藏上傳區域，顯示結果區域
    this.uploadContainer.classList.add('hidden');
    this.optionsContainer.classList.add('hidden');
    this.processingContainer.classList.add('hidden');
    this.resultContainer.classList.remove('hidden');

    // 設定原始圖片預覽
    const originalImagePreview = this.resultContainer.querySelector('#originalImagePreview') as HTMLImageElement;
    if (originalImagePreview) {
      originalImagePreview.src = originalDataUrl;
    }

    // 初始化 Canvas 顯示預處理後的圖片
    this.initializeCanvas(processedDataUrl);

    // 顯示區段
    this.section.show();
  }

  // ========================================================================
  // 私有方法 - UI 建構
  // ========================================================================

  private createUploadContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'uploadContainer';
    return container;
  }

  private createOptionsContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'mt-4 space-y-3';
    return container;
  }

  private createProcessingContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'hidden py-8';
    container.innerHTML = `
      <div class="flex flex-col items-center">
        <div class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent mb-4"></div>
        <p class="text-gray-600">正在處理圖片...</p>
      </div>
    `;
    return container;
  }

  private createResultContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'hidden';

    container.innerHTML = `
      <!-- 預處理結果區塊（與 HTML 版本一致的邊框和 padding） -->
      <div class="border border-gray-200 rounded-lg p-4 bg-white">
        <!-- 圖片比較區域（上下排列） -->
        <div class="space-y-4" id="imageComparisonGrid">
        <!-- 原始圖片 -->
        <div class="space-y-2">
          <h4 class="text-sm font-medium text-gray-700">原始圖片</h4>
          <div id="originalImageContainer" class="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
            <img id="originalImagePreview" class="w-full max-h-64 object-contain" alt="原始圖片" style="display: block;" />
          </div>
        </div>
        <!-- 預處理結果及工具 -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h4 class="text-sm font-medium text-gray-700">預處理結果</h4>
            <!-- 縮放控制 -->
            <div class="flex items-center gap-1" id="zoomControls">
              <button type="button" id="zoomOutBtn" class="p-1 rounded hover:bg-gray-200 text-gray-600" title="縮小">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              </button>
              <span id="zoomLevel" class="text-xs text-gray-500 min-w-[40px] text-center">100%</span>
              <button type="button" id="zoomInBtn" class="p-1 rounded hover:bg-gray-200 text-gray-600" title="放大">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </button>
              <button type="button" id="zoomResetBtn" class="p-1 rounded hover:bg-gray-200 text-gray-600 ml-1" title="重設縮放">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
          <div id="processedImageContainer" class="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50">
            <canvas id="preprocessCanvas" class="w-full cursor-crosshair"></canvas>
          </div>
          <!-- 工具列 -->
          <div class="flex items-center gap-2" id="modeButtonsContainer">
            <span class="text-sm text-gray-600 mr-2">工具：</span>
          </div>
          <!-- 提示和操作按鈕 -->
          <div class="flex items-center gap-4">
            <div class="flex-1">
              <p class="text-sm text-gray-600" id="canvasHint">
                選擇工具後，點擊設定起點，再點擊設定終點
              </p>
            </div>
            <div id="actionButtonsContainer" class="flex gap-2"></div>
          </div>
        </div>
      </div>
    `;

    return container;
  }

  private createErrorContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'hidden';
    container.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-sm font-medium text-red-800">處理失敗</h3>
            <p class="mt-1 text-sm text-red-700" id="errorMessage">發生未知錯誤</p>
          </div>
          <div class="ml-auto pl-3">
            <button type="button" class="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none" id="closeErrorBtn">
              <span class="sr-only">關閉</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;

    // 綁定關閉按鈕事件
    const closeBtn = container.querySelector('#closeErrorBtn');
    closeBtn?.addEventListener('click', () => this.hideError());

    return container;
  }

  private assembleUI(): void {
    const content = this.section.getContent();

    // 上傳區域
    this.uploadZone.mount(this.uploadContainer);
    content.appendChild(this.uploadContainer);

    // 選項
    const checkboxContainer = document.createElement('div');
    checkboxContainer.className = 'flex items-center gap-4';
    this.removeTextCheckbox.mount(checkboxContainer);
    this.hasDimensionsCheckbox.mount(checkboxContainer);
    this.optionsContainer.appendChild(checkboxContainer);

    // 開始按鈕
    const startBtnContainer = document.createElement('div');
    this.startButton = new Button('開始預處理', {
      variant: 'primary',
      disabled: true,
      onClick: () => this.startPreprocessing(),
    });
    this.startButton.mount(startBtnContainer);
    this.optionsContainer.appendChild(startBtnContainer);

    content.appendChild(this.optionsContainer);
    content.appendChild(this.errorContainer);
    content.appendChild(this.processingContainer);
    content.appendChild(this.resultContainer);

    // 建立模式按鈕
    this.createModeButtons();

    // 建立操作按鈕
    this.createActionButtons();
  }

  private createModeButtons(): void {
    const container = this.resultContainer.querySelector('#modeButtonsContainer');
    if (!container) return;

    const modes: { mode: DrawingMode; label: string }[] = [
      { mode: 'dimension-select', label: '選取長度參考線' },
      { mode: 'reference', label: '手動畫長度參考線' },
      { mode: 'eraser', label: '橡皮擦' },
      { mode: 'blackline', label: '畫黑線' },
    ];

    for (const { mode, label } of modes) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300';
      btn.textContent = label;
      btn.addEventListener('click', () => this.setMode(mode));

      this.modeButtons.set(mode, btn);
      container.appendChild(btn);
    }

    // 新增「設為參考線」按鈕（當選取尺寸後才顯示）
    const useAsRefBtn = document.createElement('button');
    useAsRefBtn.type = 'button';
    useAsRefBtn.id = 'useAsReferenceBtn';
    useAsRefBtn.className = 'hidden px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700';
    useAsRefBtn.textContent = '設為參考線';
    useAsRefBtn.addEventListener('click', () => {
      if (useSelectedDimensionAsReference()) {
        // 成功設定參考線後，切換回 none 模式
        setDrawingMode('none');
      }
    });
    container.appendChild(useAsRefBtn);
  }

  private createActionButtons(): void {
    const container = this.resultContainer.querySelector('#actionButtonsContainer');
    if (!container) return;

    const confirmBtn = new Button('確認並繼續', {
      variant: 'primary',
      onClick: () => this.confirmPreprocessing(),
    });

    const resetBtn = new Button('重新上傳', {
      variant: 'secondary',
      onClick: () => this.reset(),
    });

    resetBtn.mount(container as HTMLElement);
    confirmBtn.mount(container as HTMLElement);
  }

  // ========================================================================
  // 私有方法 - 事件處理
  // ========================================================================

  private handleFileSelect(file: File, base64: string): void {
    this.originalFile = file;
    this.originalImageDataUrl = base64;

    // 清除當前載入的規劃 ID（上傳新平面圖表示開始新規劃）
    import('@/store/planner-store').then(({ clearLoadedPlanId }) => {
      clearLoadedPlanId();
    });

    // 取得圖片尺寸並儲存到 store
    const img = new Image();
    img.onload = () => {
      // 將 File 轉換為 Blob（File 本身就是 Blob 的子類型）
      setOriginalImage(file, base64, file, img.width, img.height);
    };
    img.src = base64;

    // 啟用開始按鈕
    this.startButton?.setDisabled(false);
  }

  private async startPreprocessing(): Promise<void> {
    if (!this.originalFile) return;

    // 隱藏之前的錯誤訊息
    this.hideError();

    // 取得 checkbox 狀態
    const removeText = this.removeTextCheckbox.isChecked();
    const hasDimensions = this.hasDimensionsCheckbox.isChecked();

    // 如果兩個都沒勾選，直接使用原始圖片進入下一步
    if (!removeText && !hasDimensions) {
      await this.useOriginalImage();
      return;
    }

    // 顯示處理中
    this.uploadContainer.classList.add('hidden');
    this.optionsContainer.classList.add('hidden');
    this.processingContainer.classList.remove('hidden');

    try {
      let imageDataUrl: string;
      let detectedDimensions: FrontendDetectedDimension[] = [];

      if (removeText) {
        // 需要 Gemini 清理：呼叫完整預處理 API
        const params: PreprocessParams = {
          image: this.originalFile,
          removeText: true,
          detectDimensions: hasDimensions,
        };

        const result = await preprocessImage(params);

        // 加上 data URL 前綴（如果沒有的話）
        imageDataUrl = result.imageBase64.startsWith('data:')
          ? result.imageBase64
          : `data:image/png;base64,${result.imageBase64}`;

        detectedDimensions = result.detectedDimensions;
      } else {
        // 只需要尺寸偵測（不做 Gemini 清理）
        // 先取得原始圖片的 data URL
        imageDataUrl = await this.fileToDataUrl(this.originalFile);

        // 呼叫尺寸偵測 API
        detectedDimensions = await detectDimensionsOnly(this.originalFile);
      }

      setProcessedImage(imageDataUrl);

      // 儲存偵測到的尺寸
      if (detectedDimensions.length > 0) {
        setDetectedDimensions(detectedDimensions);
      }

      // 顯示結果
      this.processingContainer.classList.add('hidden');
      this.resultContainer.classList.remove('hidden');

      // 初始化 Canvas
      this.initializeCanvas(imageDataUrl);
    } catch (error) {
      console.error('Preprocessing failed:', error);

      // 判斷錯誤類型並顯示對應訊息
      let errorMessage = '處理圖片時發生未知錯誤，請稍後再試。';

      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = '無法連接到後端伺服器，請確認伺服器是否正在運行。';
        } else if (error.message.includes('timeout') || error.message.includes('超時')) {
          errorMessage = '處理超時，請稍後再試或嘗試上傳較小的圖片。';
        } else {
          errorMessage = error.message;
        }
      }

      // 顯示錯誤訊息
      this.showError(errorMessage);

      // 返回上傳狀態
      this.processingContainer.classList.add('hidden');
      this.uploadContainer.classList.remove('hidden');
      this.optionsContainer.classList.remove('hidden');
    }
  }

  /**
   * 直接使用原始圖片（不呼叫任何 API）
   */
  private async useOriginalImage(): Promise<void> {
    if (!this.originalFile) return;

    const imageDataUrl = await this.fileToDataUrl(this.originalFile);
    setProcessedImage(imageDataUrl);

    // 顯示結果
    this.uploadContainer.classList.add('hidden');
    this.optionsContainer.classList.add('hidden');
    this.resultContainer.classList.remove('hidden');

    // 初始化 Canvas
    this.initializeCanvas(imageDataUrl);
  }

  /**
   * 將 File 轉換為 Data URL
   */
  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('讀取檔案失敗'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * 顯示錯誤訊息
   */
  private showError(message: string): void {
    const errorMessageEl = this.errorContainer.querySelector('#errorMessage');
    if (errorMessageEl) {
      errorMessageEl.textContent = message;
    }
    this.errorContainer.classList.remove('hidden');
    this.errorContainer.classList.add('mt-4');
  }

  /**
   * 隱藏錯誤訊息
   */
  private hideError(): void {
    this.errorContainer.classList.add('hidden');
    this.errorContainer.classList.remove('mt-4');
  }

  private initializeCanvas(imageUrl: string): void {
    const originalImagePreview = this.resultContainer.querySelector('#originalImagePreview') as HTMLImageElement;
    const canvasElement = this.resultContainer.querySelector('#preprocessCanvas') as HTMLCanvasElement;

    if (!canvasElement) return;

    this.canvas = new PreprocessCanvas({
      canvas: canvasElement,
      onReferenceLineComplete: async (_pixelLength) => {
        const result = await prompt('請輸入參考線的實際長度（公尺）', {
          title: '設定比例尺',
          placeholder: '例如：1.5',
          defaultValue: '1',
        });

        if (result !== null) {
          const meters = parseFloat(result);
          if (!isNaN(meters) && meters > 0) {
            return meters;
          }
        }
        return null;
      },
    });

    this.canvas.initialize();

    // 綁定縮放控制按鈕
    this.bindZoomControls();

    // 載入預處理後的圖片到 canvas
    const img = new Image();
    img.onload = () => {
      this.canvas?.setImage(img);
      this.canvas?.render();

      // 同步兩個容器的高度
      this.syncImageHeights();
    };
    img.src = imageUrl;

    // 設定原始圖片預覽
    if (originalImagePreview && this.originalImageDataUrl) {
      originalImagePreview.src = this.originalImageDataUrl;
      originalImagePreview.onload = () => {
        // 原始圖片載入後也同步高度
        this.syncImageHeights();
      };
    }
  }

  /**
   * 綁定縮放控制按鈕事件
   */
  private bindZoomControls(): void {
    const zoomInBtn = this.resultContainer.querySelector('#zoomInBtn');
    const zoomOutBtn = this.resultContainer.querySelector('#zoomOutBtn');
    const zoomResetBtn = this.resultContainer.querySelector('#zoomResetBtn');

    zoomInBtn?.addEventListener('click', () => {
      this.canvas?.zoom(0.2);
      this.updateZoomLevel();
    });

    zoomOutBtn?.addEventListener('click', () => {
      this.canvas?.zoom(-0.2);
      this.updateZoomLevel();
    });

    zoomResetBtn?.addEventListener('click', () => {
      this.canvas?.resetZoom();
      this.updateZoomLevel();
    });

    // 監聽 canvas 的滾輪縮放，更新顯示
    const canvasElement = this.resultContainer.querySelector('#preprocessCanvas');
    canvasElement?.addEventListener('wheel', () => {
      // 延遲更新，等待 canvas 處理完成
      setTimeout(() => this.updateZoomLevel(), 10);
    });
  }

  /**
   * 更新縮放倍率顯示
   */
  private updateZoomLevel(): void {
    const zoomLevel = this.resultContainer.querySelector('#zoomLevel');
    if (zoomLevel && this.canvas) {
      const scale = this.canvas.getUserScale();
      zoomLevel.textContent = `${Math.round(scale * 100)}%`;
    }
  }

  /**
   * 同步原始圖片和預處理結果的顯示設定
   * 注意：現在改為上下排列，不再需要同步高度
   */
  private syncImageHeights(): void {
    // 上下排列模式下，不需要同步高度
    // 保留方法以維持相容性
  }

  private setMode(mode: DrawingMode): void {
    // 切換模式時，清除選取的尺寸
    if (mode !== 'dimension-select') {
      selectDimension(null);
    }

    setDrawingMode(mode);

    // 更新按鈕樣式
    this.modeButtons.forEach((btn, m) => {
      if (m === mode) {
        btn.className = 'px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white';
      } else {
        btn.className = 'px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300';
      }
    });

    // 更新提示文字
    const hint = this.resultContainer.querySelector('#canvasHint');
    if (hint) {
      switch (mode) {
        case 'dimension-select':
          hint.textContent = '點擊綠色尺寸線段選取';
          break;
        case 'reference':
          hint.textContent = '點擊設定起點，再點擊設定終點（Esc 取消）';
          break;
        case 'eraser':
          hint.textContent = '框選要遮蓋的區域（白色覆蓋）';
          break;
        case 'blackline':
          hint.textContent = '點擊設定起點，再點擊設定終點（Esc 取消）';
          break;
        default:
          hint.textContent = '';
      }
    }
  }

  private confirmPreprocessing(): void {
    let state = preprocessStore.getState();

    // 如果有選取的尺寸但尚未設為參考線，自動設定
    if (!state.referenceLine && state.selectedDimensionId !== null) {
      useSelectedDimensionAsReference();
      // 重新取得狀態
      state = preprocessStore.getState();
    }

    if (!state.referenceLine) {
      alert('請先繪製參考線以設定比例尺');
      return;
    }

    // 產生處理後的圖片
    const processedImage = this.canvas?.generateProcessedImage() ?? '';

    // 計算像素到公尺的比例
    const refLine = state.referenceLine;
    const pixelLength = distance(refLine.start, refLine.end);
    const pixelToMeter = pixelLength / refLine.lengthMeters;

    this.options.onComplete?.(processedImage, pixelToMeter);
  }

  private reset(): void {
    this.canvas?.destroy();
    this.canvas = null;
    this.originalFile = null;
    this.originalImageDataUrl = null;

    this.uploadZone.reset();
    preprocessStore.reset();
    this.hideError();

    this.startButton?.setDisabled(true);

    // 清除原始圖片預覽
    const originalImagePreview = this.resultContainer.querySelector('#originalImagePreview') as HTMLImageElement;
    if (originalImagePreview) {
      originalImagePreview.src = '';
    }

    this.resultContainer.classList.add('hidden');
    this.processingContainer.classList.add('hidden');
    this.uploadContainer.classList.remove('hidden');
    this.optionsContainer.classList.remove('hidden');
  }

  private subscribeToStore(): void {
    this.unsubscribe = preprocessStore.subscribe((state) => {
      // 更新模式按鈕
      this.modeButtons.forEach((btn, mode) => {
        if (mode === state.drawingMode) {
          btn.className = 'px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white';
        } else {
          btn.className = 'px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300';
        }
      });

      // 更新「設為參考線」按鈕顯示狀態
      const useAsRefBtn = this.resultContainer.querySelector('#useAsReferenceBtn');
      if (useAsRefBtn) {
        if (state.selectedDimensionId !== null) {
          useAsRefBtn.classList.remove('hidden');
        } else {
          useAsRefBtn.classList.add('hidden');
        }
      }

      // 更新提示文字
      const hint = this.resultContainer.querySelector('#canvasHint');
      if (hint && state.drawingMode === 'dimension-select') {
        if (state.selectedDimensionId !== null) {
          hint.textContent = '拖曳端點調整線段位置，或點擊「設為參考線」';
        } else {
          hint.textContent = '點擊綠色尺寸線段選取';
        }
      }
    });
  }
}
